import React, {Fragment, useState, useEffect, lazy, Suspense, useCallback, useMemo} from 'react';
import {Outlet, ScrollRestoration} from 'react-router-dom';
import Footer from '../../components/footer/Footer';
import Sidebar from '../../components/UI/Sidebar';
import MobileHeader from '../../components/UI/MobileHeader';
import useAnalytics from "../../hooks/useAnalytics.tsx";
import useCookieConsent from "../../hooks/useCookieConsent.tsx";
import useScrollRestoration from "../../hooks/useScrollRestoration.tsx";
import { useKeyboardShortcuts, createNavigationShortcuts } from "../../hooks/useKeyboardShortcuts";
import { useHighContrastMode } from "../../hooks/useHighContrastMode";

// Lazy load cookie consent components
const CookieConsent = lazy(() => import('react-cookie-consent').then(module => ({
    default: module.CookieConsent
})));
const CookiePolicyModal = lazy(() => import('../../components/cookiePolicy/CookiePolicyModal'));

// Hoist CookieConsent style object to prevent recreation on every render
const COOKIE_CONSENT_STYLE = {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingRight: '1rem',
    background: '#2B373B',
    color: 'red !important',
    opacity: 0.95,
} as const;

const RootLayout: React.FC = () => {
    const [consentGiven, setConsentGiven] = useState(false);
    const [cookieLoaded, setCookieLoaded] = useState(false);
    const getKey = useScrollRestoration();
    
    // Delay loading the cookie consent dialog
    useEffect(() => {
        const timer = setTimeout(() => {
            setCookieLoaded(true);
        }, 1500); // Delay cookie consent by 1.5 seconds
        
        return () => clearTimeout(timer);
    }, []);
    
    useAnalytics();
    useCookieConsent(consentGiven);
    
    // High contrast mode support
    const { toggleHighContrast } = useHighContrastMode();

    // Keyboard shortcuts for navigation
    const focusNavigation = useCallback(() => {
        const navElement = document.querySelector('nav[aria-label="Main navigation"]');
        const firstNavLink = navElement?.querySelector('a') as HTMLElement;
        firstNavLink?.focus();
    }, []);

    const focusMainContent = useCallback(() => {
        const mainContent = document.getElementById('main-content');
        mainContent?.focus();
    }, []);
    
    const navigationShortcuts = useMemo(() => [
        ...createNavigationShortcuts(focusNavigation, focusMainContent),
        {
            key: 'h',
            altKey: true,
            shiftKey: true,
            action: toggleHighContrast,
            description: 'Toggle high contrast mode'
        }
    ], [focusNavigation, focusMainContent, toggleHighContrast]);

    useKeyboardShortcuts(navigationShortcuts);

    return (
        <Fragment>
            <a href="#main-content" className="skip-link">
                Skip to main content
            </a>
            <MobileHeader />
            <div className='grid min-h-screen min-w-0 grid-cols-1 md:grid-cols-[64px_1fr] lg:grid-cols-[80px_1fr] 2xl:grid-cols-[96px_1fr]'>
                <header className="hidden min-w-0 md:block">
                    <Sidebar/>
                </header>
                <div className='min-w-0 md:col-start-2 col-span-full'>
                    <main id="main-content" role="main" tabIndex={-1}>
                        <Outlet/>
                    </main>
                    
                    {cookieLoaded && (
                        <Suspense fallback={<div></div>}>
                            <CookieConsent
                                location="bottom"
                                buttonText="Got it"
                                style={COOKIE_CONSENT_STYLE}
                                contentClasses='contentCookieConsent'
                                disableButtonStyles={true}
                                onAccept={() => setConsentGiven(true)}
                                buttonClasses='buttonCookieConsent'
                                expires={150}
                            >
                                <div>
                                    <span className='text-white'>
                                        This website uses third-party cookies, including Google
                                        Analytics, to ensure you get the best experience and to analyze
                                        site traffic. By continuing to use this site, you consent to our
                                        use of cookies.
                                    </span>
                                    <span className="flex items-baseline text-white">
                                        Learn more in our
                                        <Suspense fallback={<div>Loading...</div>}>
                                            <CookiePolicyModal/>
                                        </Suspense>
                                        &nbsp;.
                                    </span>
                                </div>
                            </CookieConsent>
                        </Suspense>
                    )}
                    
                    <Footer/>
                </div>
            </div>
            <ScrollRestoration getKey={getKey}/>
        </Fragment>
    );
};

export default RootLayout;

